import React, { useEffect } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import TextField from '@material-ui/core/TextField'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup'
import SegmentedToggleButton from 'components/segmented-toggle-button'
import { makeStyles } from '@material-ui/core/styles'
import RegistrationCloseDay from 'pages/pga-coach/bookings/events/edit/edit-event-form/form-sections/registration-close-day'
import RegistrationOpenDateTime from 'pages/pga-coach/bookings/events/edit/edit-event-form/form-sections/registration-open-datetime'
import MaxAttendance from 'pages/pga-coach/bookings/events/edit/edit-event-form/form-sections/max-attendance'
import AccessCode from 'pages/pga-coach/bookings/events/edit/edit-event-form/form-sections/access-code'
import CreateEventWaitlist from './create-event-waitlist'
import { useStyles } from 'pages/pga-coach/bookings/events/edit/edit-event-form/styles'

const useRegistrationOptionStyles = makeStyles(theme => ({
  optionsColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    width: '100%',
    paddingTop: theme.spacing(1),
  },
  optionLabel: {
    alignItems: 'center',
    margin: 0,
    '& .MuiFormControlLabel-label': {
      marginLeft: theme.spacing(0.5),
    },
  },
  optionTitle: {
    fontSize: '1rem',
    fontWeight: 400,
    color: theme.palette.text.primary,
    lineHeight: 1.25,
  },
  radio: {
    padding: theme.spacing(1),
    color: '#70787C',
    '&.Mui-checked': {
      color: '#0053FF',
    },
  },
}))

const REGISTRATION_OPTIONS = [
  { value: 'pga-coach', title: 'PGA Coach' },
  { value: 'external', title: 'External URL' },
]

const CreateEventRegistration = ({ eventStartDateTime, timezone }) => {
  const classes = useStyles()
  const registrationOptionClasses = useRegistrationOptionStyles()
  const { register, errors, control, watch, setValue } = useFormContext()
  const isExternalRegistration = watch('isExternalRegistration')

  useEffect(() => {
    if (isExternalRegistration) {
      setValue('unlisted', false)
    }
  }, [isExternalRegistration, setValue])

  return (
    <>
      <Box className={classes.fieldGroup}>
        <Typography className={classes.sectionHeading}>
          Registration collection
        </Typography>
        <Typography className={classes.fieldHelper}>
          Use PGA Coach to collect registrations, or send students to an
          external URL.
        </Typography>
        <Controller
          name="isExternalRegistration"
          control={control}
          render={({ value, onChange }) => (
            <RadioGroup
              className={registrationOptionClasses.optionsColumn}
              value={value ? 'external' : 'pga-coach'}
              onChange={event => onChange(event.target.value === 'external')}
              aria-label="Registration collection"
            >
              {REGISTRATION_OPTIONS.map(option => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  className={registrationOptionClasses.optionLabel}
                  control={
                    <Radio
                      className={registrationOptionClasses.radio}
                      color="primary"
                    />
                  }
                  label={
                    <Typography
                      className={registrationOptionClasses.optionTitle}
                    >
                      {option.title}
                    </Typography>
                  }
                />
              ))}
            </RadioGroup>
          )}
        />
      </Box>
      {isExternalRegistration ? (
        <Box className={classes.fieldGroup}>
          <TextField
            label="External registration URL"
            placeholder="https://www.example.com"
            name="externalRegistrationUrl"
            inputRef={register}
            error={!!errors?.externalRegistrationUrl}
            helperText={errors?.externalRegistrationUrl?.message}
            variant="outlined"
            fullWidth
            className={classes.fieldInput}
          />
        </Box>
      ) : (
        <>
          <RegistrationOpenDateTime />
          <RegistrationCloseDay
            eventStartDateTime={eventStartDateTime}
            isExternalRegistration={false}
            timezone={timezone}
          />
          <MaxAttendance />
          <CreateEventWaitlist />
          <AccessCode />
        </>
      )}
      <Box className={classes.fieldGroup}>
        <Typography className={classes.sectionHeading}>
          Collect RSVPs
        </Typography>
        <Typography className={classes.fieldHelper}>
          Track who plans to attend without collecting payment through PGA
          Coach.
        </Typography>
        <Controller
          name="collectAttendance"
          control={control}
          render={({ value, onChange }) => (
            <ToggleButtonGroup
              exclusive
              value={value ? 'yes' : 'no'}
              onChange={(_, nextValue) => {
                if (!nextValue) return
                onChange(nextValue === 'yes')
              }}
              aria-label="Collect RSVPs"
            >
              <SegmentedToggleButton
                value="yes"
                groupValue={value ? 'yes' : 'no'}
              >
                Yes
              </SegmentedToggleButton>
              <SegmentedToggleButton
                value="no"
                groupValue={value ? 'yes' : 'no'}
              >
                No
              </SegmentedToggleButton>
            </ToggleButtonGroup>
          )}
        />
      </Box>
    </>
  )
}

export default CreateEventRegistration
